import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Custom hook for managing view routing with transitions
 * 
 * Handles hash-based routing (#chat, #taxper, #contly) with smooth transitions
 */
export const useViewRouter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [view, setView] = useState('chat');
  const [isViewTransitioning, setIsViewTransitioning] = useState(false);
  const [viewOpacity, setViewOpacity] = useState(1);
  const isInitialMount = useRef(true);

  // Handle hash routing
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && ['chat', 'taxper', 'contly'].includes(hash)) {
      // Skip transition on initial mount
      if (isInitialMount.current) {
        setView(hash);
        isInitialMount.current = false;
        return;
      }
      
      // Trigger transition animation
      setIsViewTransitioning(true);
      setViewOpacity(0);
      
      // After fade out, change view and fade in
      setTimeout(() => {
        setView(hash);
        setTimeout(() => {
          setViewOpacity(1);
          setIsViewTransitioning(false);
        }, 50);
      }, 200);
    } else {
      if (!isInitialMount.current) {
        // Only transition if not initial mount
        setIsViewTransitioning(true);
        setViewOpacity(0);
        setTimeout(() => {
          setView('chat');
          setTimeout(() => {
            setViewOpacity(1);
            setIsViewTransitioning(false);
          }, 50);
        }, 200);
      } else {
        setView('chat');
        isInitialMount.current = false;
      }
    }
  }, [location.hash]);

  const handleViewChange = (newView) => {
    // Trigger transition animation
    setIsViewTransitioning(true);
    setViewOpacity(0);
    
    // After fade out, change view and fade in
    setTimeout(() => {
      setView(newView);
      navigate(`/app#${newView}`);
      setTimeout(() => {
        setViewOpacity(1);
        setIsViewTransitioning(false);
      }, 50);
    }, 200);
  };

  // Initialize view opacity on mount
  useEffect(() => {
    setViewOpacity(1);
  }, []);

  return {
    view,
    isViewTransitioning,
    viewOpacity,
    handleViewChange,
  };
};

